import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// 'themeUserSet' is written only when the user explicitly toggles the theme widget.
// When it is absent, the site always follows the current OS preference so that a stale
// auto-detected localStorage value never overrides a changed system setting.
const THEME_KEY = 'theme';
const THEME_USER_SET_KEY = 'themeUserSet';

function hasUserSetTheme() {
  return localStorage.getItem(THEME_USER_SET_KEY) === '1';
}

export function ThemeProvider({ children }) {
  const [darkMode, setDarkMode] = useState(() => {
    // If the user has never explicitly toggled, always derive from the current OS
    // preference rather than a potentially stale localStorage value.
    const isDark = hasUserSetTheme()
      ? localStorage.getItem(THEME_KEY) === 'dark'
      : window.matchMedia('(prefers-color-scheme: dark)').matches;

    // Apply synchronously so there's no render before data-theme is set
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    return isDark;
  });

  // Mirror state into the DOM attribute. Only write localStorage when the user
  // has made an explicit choice — otherwise we'd re-bake a stale preference on
  // every page load and defeat the OS-preference check above.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    if (hasUserSetTheme()) {
      localStorage.setItem(THEME_KEY, darkMode ? 'dark' : 'light');
    }
  }, [darkMode]);

  // Follow live OS theme changes when the user has not made an explicit choice.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (!hasUserSetTheme()) {
        setDarkMode(e.matches);
      }
    };
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  // Legacy cleanup from the old data-light-look theme system.
  useEffect(() => {
    document.documentElement.removeAttribute('data-light-look');
    localStorage.removeItem('light-look');
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const next = !prev;
      // Record that this is now an explicit user preference and persist it.
      localStorage.setItem(THEME_USER_SET_KEY, '1');
      localStorage.setItem(THEME_KEY, next ? 'dark' : 'light');
      return next;
    });
  };

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
